import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Home as HomeIcon, Image as ImageIcon, Star, ChevronDown, ExternalLink, HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import AvailabilityCalendar from '@/components/AvailabilityCalendar';
import axios from 'axios';
import SEOSchema from '@/components/SEOSchema';

const API = '/api';

// Track event helper
const trackEvent = (eventType, extra = {}) => {
  axios.post(`${API}/stats/track`, { event_type: eventType, ...extra }).catch(() => {});
};

// Default FAQ Data (fallback) - Including location and booking questions
const defaultFaqData = [
  {
    question: "Does the villa have a heated pool?",
    answer: "Yes! Our villa features a luxurious heated swimming pool surrounded by the historic Renaissance garden. The pool is maintained at a comfortable temperature year-round, perfect for a refreshing swim even during cooler months."
  },
  {
    question: "Can we embark private boat tours right in front of the villa?",
    answer: "Yes! One of the unique advantages of our waterfront location is direct sea access. You can arrange private boat tours, kayaking adventures, or simply enjoy swimming in the crystal-clear Adriatic waters right from our private waterfront area."
  },
  {
    question: "What makes the garden historically significant?",
    answer: "Our garden dates back to the 16th century and represents one of the finest examples of Renaissance garden design in the Dubrovnik region. It spans over 1,000 square meters and features original stone pathways, heritage Mediterranean plants, and stunning sea views."
  },
  {
    question: "Is the villa suitable for Instagram photography?",
    answer: "Absolutely! Renaissance Garden Dubrovnik is one of the most Instagrammable locations in Dubrovnik. From the historic garden archways to the waterfront sunset views and the stunning pool area, every corner offers picture-perfect moments."
  },
  {
    question: "How many guests can the villa accommodate?",
    answer: "The villa comfortably accommodates up to 6 guests in 3 beautifully appointed bedrooms. Each room features luxury linens, air conditioning, and views of either the garden or the Adriatic Sea."
  },
  {
    question: "Where exactly is Renaissance Garden Dubrovnik located?",
    answer: "The villa is located in Mokošica, approximately 5 kilometres from Dubrovnik Old Town. It sits directly on the Adriatic waterfront with private sea access and stunning views of the Dubrovnik coastline."
  },
  {
    question: "Is Renaissance Garden Dubrovnik available for direct booking?",
    answer: "Yes! You can check availability and send a direct inquiry via our contact page, or book through our Airbnb listing. Direct inquiries are welcome for longer stays and special occasions."
  }
];

// Default content (fallback) - using new key naming convention
const defaultContent = {
  homepage_hero_title: { value: "Historic Renaissance Villa with Modern Luxury Amenities" },
  homepage_hero_subtitle: { value: "16th-century waterfront villa with heated pool, 1000m² Renaissance garden and Adriatic sea views — Mokošica, Dubrovnik, Croatia" },
  homepage_story_heading: { value: "The Most Instagrammable Waterfront View in Dubrovnik" },
  homepage_story_paragraph_1: { value: "This unique waterfront Villa blends historic charm with contemporary comforts. Its Renaissance Garden showcases the elegance of Dubrovnik's historic summer residences and is among the best preserved in the region." },
  homepage_story_paragraph_2: { value: "Vine-covered walkways wind through the 1000 m² garden surrounded by historic walls. Chosen centuries ago as a retreat, it's the perfect place to relax where timeless beauty lives." },
  homepage_seo_paragraph: { value: "Renaissance Garden Dubrovnik is a historic 16th-century heritage villa located in Mokošica, Dubrovnik, Croatia. The property features a 1000 square meter Renaissance garden, a heated outdoor swimming pool, direct waterfront sea access, and three luxury bedrooms accommodating up to 6 guests. The villa is one of the best-preserved examples of Renaissance garden architecture in the Dubrovnik-Neretva region, offering private boat access, Adriatic sea views, and vine-covered stone walkways. Rated 5.0 stars by over 57 guests on Airbnb." },
  homepage_highlights_heading: { value: "Experience Villa Renaissance" },
  homepage_highlight_1_title: { value: "Historic Villa" },
  homepage_highlight_1_description: { value: "16th-century architecture beautifully restored with modern amenities" },
  homepage_highlight_2_title: { value: "1000 m² Garden" },
  homepage_highlight_2_description: { value: "Explore centuries-old pathways through a stunning Renaissance garden" },
  homepage_highlight_3_title: { value: "Heated Pool" },
  homepage_highlight_3_description: { value: "Relax by the pool with breathtaking views of the Adriatic Sea" },
  homepage_reviews_heading: { value: "What Our Guests Say" },
  homepage_faq_heading: { value: "Frequently Asked Questions" },
  homepage_faq_subheading: { value: "Everything you need to know about your stay at Renaissance Garden Dubrovnik" },
  // Legacy keys for backward compatibility
  hero_title: { value: "Historic Renaissance Villa with Modern Luxury Amenities" },
  hero_subtitle: { value: "16th-century waterfront villa with heated pool, 1000m² Renaissance garden and Adriatic sea views — Mokošica, Dubrovnik, Croatia" },
  story_title: { value: "The Most Instagrammable Waterfront View in Dubrovnik" },
  story_text_1: { value: "This unique waterfront Villa blends historic charm with contemporary comforts. Its Renaissance Garden showcases the elegance of Dubrovnik's historic summer residences and is among the best preserved in the region." },
  story_text_2: { value: "Vine-covered walkways wind through the 1000 m² garden surrounded by historic walls. Chosen centuries ago as a retreat, it's the perfect place to relax where timeless beauty lives." },
  highlights_title: { value: "Experience Villa Renaissance" },
  highlight_1_title: { value: "Historic Villa" },
  highlight_1_text: { value: "16th-century architecture beautifully restored with modern amenities" },
  highlight_2_title: { value: "1000 m² Garden" },
  highlight_2_text: { value: "Explore centuries-old pathways through a stunning Renaissance garden" },
  highlight_3_title: { value: "Heated Pool" },
  highlight_3_text: { value: "Relax by the pool with breathtaking views of the Adriatic Sea" },
  reviews_title: { value: "What Our Guests Say" },
  faq_title: { value: "Frequently Asked Questions" },
  faq_subtitle: { value: "Everything you need to know about your stay at Renaissance Garden Dubrovnik" },
};

// Simple markdown parser helper
const parseMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*?)$/gm, '• $1')
    .replace(/\n/g, '<br/>');
};

const HomePage = () => {
  const location = useLocation();
  const [content, setContent] = useState(defaultContent);
  const [faqData, setFaqData] = useState(defaultFaqData);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch dynamic content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        if (response.data.content && Object.keys(response.data.content).length > 0) {
          setContent({ ...defaultContent, ...response.data.content });
        }
        if (response.data.faqs && response.data.faqs.length > 0) {
          setFaqData(response.data.faqs);
        }
      } catch (error) {
        console.log('Using default content');
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Track page visit
  useEffect(() => {
    trackEvent('visit', { page: 'home' });
  }, []);

  useEffect(() => {
    if (location.hash === '#reviews') {
      setTimeout(() => {
        const element = document.getElementById('reviews');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (location.hash === '#faq') {
      setTimeout(() => {
        const element = document.getElementById('faq');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Helper to get content value (supports both new and legacy keys)
  const c = (key) => {
    // Try new key format first, then legacy
    const newKey = key.startsWith('homepage_') ? key : `homepage_${key}`;
    const legacyKey = key.replace('homepage_', '');
    
    return content[newKey]?.value || content[key]?.value || content[legacyKey]?.value || 
           defaultContent[newKey]?.value || defaultContent[key]?.value || defaultContent[legacyKey]?.value || '';
  };

  return (
    <div className="stone-texture" data-testid="home-page">
      {/* JSON-LD Schema Markup */}
      <SEOSchema faqData={faqData} />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://a0.muscache.com/im/pictures/ad043591-a47e-4f6e-976a-90378deba998.jpg?im_w=1200')",
          }}
        >
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl animate-fade-in-up">
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg" data-testid="hero-title">
            {c('homepage_hero_title') || c('hero_title')}
          </h1>
          
          {/* Hero Trust Badge */}
          <a 
            href="https://www.airbnb.com/rooms/36517649?source_impression_id=p3_1774275773_P36zG1iQyJKkzDXq"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-sm transition-all mb-6 text-sm"
            data-testid="hero-trust-badge"
            onClick={() => trackEvent('booking_click_airbnb')}
          >
            <svg 
              className="w-5 h-5" 
              viewBox="0 0 32 32" 
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M16 1c-1.663 0-3.005 1.342-3.005 3.005 0 .775.301 1.477.785 2.01-.484.533-.785 1.235-.785 2.01 0 1.663 1.342 3.005 3.005 3.005s3.005-1.342 3.005-3.005c0-.775-.301-1.477-.785-2.01.484-.533.785-1.235.785-2.01 0-1.663-1.342-3.005-3.005-3.005zm8.23 9.655c-1.379-1.379-3.614-1.379-4.993 0l-3.237 3.237-3.237-3.237c-1.379-1.379-3.614-1.379-4.993 0s-1.379 3.614 0 4.993l8.23 8.23 8.23-8.23c1.379-1.379 1.379-3.614 0-4.993z"/>
            </svg>
            <span className="font-medium">Find us on Airbnb — Superhost Verified</span>
            <ExternalLink size={14} />
          </a>
          
          <p className="text-lg sm:text-xl mb-8 drop-shadow-md" data-testid="hero-subtitle">
            {c('homepage_hero_subtitle') || c('hero_subtitle')}
          </p>
          <Link to="/contact" data-testid="hero-cta" onClick={() => trackEvent('booking_click_direct')}>
            <Button className="bg-secondary hover:bg-secondary/90 text-white px-8 py-6 text-lg rounded-sm transition-all hover:scale-105">
              Check Availability
            </Button>
          </Link>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-white" size={32} />
        </div>
      </section>

      {/* SEO Description Section - Fully crawlable, subtle design */}
      <section className="py-12 bg-muted/50" data-testid="seo-description">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-sm text-muted-foreground leading-relaxed text-center">
            {c('homepage_seo_paragraph') || "Renaissance Garden Dubrovnik is a historic 16th-century heritage villa located in Mokošica, Dubrovnik, Croatia. The property features a 1000 square meter Renaissance garden, a heated outdoor swimming pool, direct waterfront sea access, and three luxury bedrooms accommodating up to 6 guests. The villa is one of the best-preserved examples of Renaissance garden architecture in the Dubrovnik-Neretva region, offering private boat access, Adriatic sea views, and vine-covered stone walkways. Rated 5.0 stars by over 57 guests on Airbnb."}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary" data-testid="story-heading">
                {c('homepage_story_heading') || c('story_title')}
              </h2>
              <p 
                className="text-lg text-muted-foreground leading-relaxed" 
                data-testid="story-text"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(c('homepage_story_paragraph_1') || c('story_text_1')) }}
              />
              <p 
                className="text-lg text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(c('homepage_story_paragraph_2') || c('story_text_2')) }}
              />
              <div className="flex items-center space-x-2 text-primary">
                <MapPin size={20} />
                <span className="font-medium" data-testid="location">Mokošica, Dubrovnik, Croatia</span>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://a0.muscache.com/im/pictures/caeec492-61c4-4449-a4d8-ff805faa06a2.jpg?im_w=1200"
                alt="Historic Renaissance garden with stone pathways and Mediterranean plants in Dubrovnik villa"
                className="rounded-none shadow-lg hover:scale-105 transition-transform duration-500"
                data-testid="story-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-24 bg-muted" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-center mb-16" data-testid="highlights-heading">
            {c('homepage_highlights_heading') || c('highlights_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-border hover:shadow-lg transition-shadow" data-testid="highlight-card-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <HomeIcon className="text-primary" size={32} />
                </div>
                <h3 className="font-playfair text-xl font-semibold mb-3">{c('homepage_highlight_1_title') || c('highlight_1_title')}</h3>
                <p className="text-muted-foreground">
                  {c('homepage_highlight_1_description') || c('highlight_1_text')}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border hover:shadow-lg transition-shadow" data-testid="highlight-card-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ImageIcon className="text-secondary" size={32} />
                </div>
                <h3 className="font-playfair text-xl font-semibold mb-3">{c('homepage_highlight_2_title') || c('highlight_2_title')}</h3>
                <p className="text-muted-foreground">
                  {c('homepage_highlight_2_description') || c('highlight_2_text')}
                </p>
              </CardContent>
            </Card>
            <Card className="border-border hover:shadow-lg transition-shadow" data-testid="highlight-card-3">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="text-primary" size={32} />
                </div>
                <h3 className="font-playfair text-xl font-semibold mb-3">{c('homepage_highlight_3_title') || c('highlight_3_title')}</h3>
                <p className="text-muted-foreground">
                  {c('homepage_highlight_3_description') || c('highlight_3_text')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Availability Calendar Widget */}
      <section className="py-24" data-testid="availability-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold mb-4">Check Our Availability</h2>
            <p className="text-muted-foreground text-lg">
              See real-time availability synced directly from Airbnb
            </p>
          </div>
          <AvailabilityCalendar />
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-24 bg-muted" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-center mb-4">{c('homepage_reviews_heading') || c('reviews_title')}</h2>
          <p className="text-center text-muted-foreground mb-16">Rated 5.0 out of 5 stars by 57 guests</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-border" data-testid="review-card-1">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-secondary fill-secondary" size={20} />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">
                  "An absolute gem! The garden is breathtaking and the villa itself is like stepping back in
                  time. Perfect for a romantic getaway."
                </p>
                <p className="font-medium">- Sarah M., United Kingdom</p>
              </CardContent>
            </Card>
            <Card className="border-border" data-testid="review-card-2">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-secondary fill-secondary" size={20} />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">
                  "Slavica and Marijo were wonderful hosts. The property exceeded our expectations. The pool
                  area and gardens are magnificent!"
                </p>
                <p className="font-medium">- Michael R., United States</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Airbnb Reviews CTA */}
          <div className="text-center" data-testid="airbnb-reviews-cta">
            <a
              href="https://www.airbnb.com/rooms/36517649/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button 
                className="bg-secondary hover:bg-secondary/90 text-white px-8 py-6 text-base rounded-sm transition-all hover:scale-105 font-playfair"
                data-testid="airbnb-reviews-button"
              >
                Read all 50+ Five-Star Reviews on Airbnb
                <ExternalLink className="ml-2" size={18} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section - JSON-LD Schema only (no inline microdata) */}
      <section id="faq" className="py-24 bg-muted/30" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold mb-4">{c('homepage_faq_heading') || c('faq_title')}</h2>
            <p className="text-muted-foreground text-lg">{c('homepage_faq_subheading') || c('faq_subtitle')}</p>
          </div>
          
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div 
                key={faq.id || index}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`faq-item-${index + 1}`}
              >
                <h3 className="font-semibold text-lg mb-3 flex items-start gap-3">
                  <HelpCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                  {faq.question}
                </h3>
                <p 
                  className="text-muted-foreground pl-8"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(faq.answer) }}
                />
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">Have more questions?</p>
            <Link to="/contact">
              <Button className="bg-primary hover:bg-primary/90 rounded-sm">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;